import {
  CoinsHistoryTransaction_Event,
  CoinsHistoryTransaction_Event_Confirmed,
  CoinsHistoryTransaction_Event_Pending,
} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/coins_history_transaction.types';
import {useQuantityFormat} from '@aboutyou/i18n';
import {COLORS, SPACING, Text} from '@aboutyou/ui-components';
import {styled} from '@linaria/react';
import React from 'react';
import {ExpiryLabel} from './ExpiryLabel';

export const TransactionEventList: React.FC<{
  events: CoinsHistoryTransaction_Event[];
}> = ({events}) => {
  return (
    <EventListWrapper>
      {events.map((event, index) => {
        switch (event.type?.$case) {
          case 'confirmed':
            return (
              <TransactionEvent key={index} event={event.type.confirmed} />
            );
          case 'pending':
            return <TransactionEvent key={index} event={event.type.pending} />;
          default:
            return null;
        }
      })}
    </EventListWrapper>
  );
};

const isPending = (
  event:
    | CoinsHistoryTransaction_Event_Confirmed
    | CoinsHistoryTransaction_Event_Pending,
): event is CoinsHistoryTransaction_Event_Pending => {
  return 'availableFrom' in event;
};

const TransactionEvent: React.FC<{
  event:
    | CoinsHistoryTransaction_Event_Confirmed
    | CoinsHistoryTransaction_Event_Pending;
}> = ({event}) => {
  const pending = isPending(event);
  const format = useQuantityFormat();

  return (
    <>
      <TransactionEventWrapper>
        <TransactionEventDescription size="px12" style={{flex: 1}}>
          {event.description}
        </TransactionEventDescription>
        <BalanceWrapper>
          <BalanceChange
            size="px12"
            data-is-positive={event.balanceChange > 0 && !pending}
          >
            {event.balanceChange > 0 ? '+' : ''}
            {format(event.balanceChange)}
          </BalanceChange>
          <BalanceStatus size="px12">{event.status}</BalanceStatus>
        </BalanceWrapper>
      </TransactionEventWrapper>
      {event.subline && (
        <TransactionEventSublineWrapper size="px12" color="Text.Grey">
          {event.subline}
        </TransactionEventSublineWrapper>
      )}
      {'availableFrom' in event && (
        <AvailableFromWrapper>
          <Text size="px10">{event.availableFrom}</Text>
        </AvailableFromWrapper>
      )}
      {'expiryLabel' in event && event.expiryLabel && (
        <StyledExpiryLabel expiryLabel={event.expiryLabel} />
      )}
    </>
  );
};

const TransactionEventDescription = styled(Text)`
  flex: 1;
  line-height: 1.5;
`;

const TransactionEventSublineWrapper = styled(Text)`
  margin-top: -${SPACING['Y.Md']};
`;

const BalanceStatus = styled(Text)`
  position: relative;
  line-height: 1.5;
`;

const AvailableFromWrapper = styled.div`
  border-radius: 0.25rem;

  z-index: 1;
  position: relative;
  margin-top: 0.5rem;
  padding: 0.25rem;
  flex: 1;
  background-color: ${COLORS['Fill.Grey.Primary']};
  display: flex;
  justify-content: center;
  align-items: center;

  &::before {
    content: '';
    width: 0.5rem;
    height: 0.5rem;
    background-color: ${COLORS['Alerts.Info.Primary']};
    border-radius: 50%;
    margin-right: 0.38rem;
  }

  &::after {
    content: '';
    position: absolute;
    right: 16px;
    display: block;
    border-right: 5px solid ${COLORS['Fill.Grey.Primary']};
    border-bottom: 5px solid ${COLORS['Fill.Grey.Primary']};
    width: 0.5rem;
    height: 0.5rem;
    top: 0;
    transform: translate(-50%, -50%) rotate(-45deg);
    background-color: ${COLORS['Fill.Grey.Primary']};
  }
`;

const BalanceWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 8px;
  min-width: 20%;
`;

const BalanceChange = styled(Text)`
  padding: 0 6px;
  border-radius: 16px;
  line-height: 1.5;

  &[data-is-positive='true'] {
    background-color: ${COLORS['Alerts.Success.Tertiary']};
    color: ${COLORS['Alerts.Success.Primary']};
  }
`;

const TransactionEventWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
`;

const EventListWrapper = styled.div`
  padding-top: 0.25rem;
  display: flex;
  gap: 0.5rem;
  flex-direction: column;
`;

const StyledExpiryLabel = styled(ExpiryLabel)`
  margin: 0;
`;
